import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import * as api from "../../../../backend/request";
import {
  getUserDetails,
  getUserRole,
} from "../../../../backend/auth";
import { toast } from "react-toastify";
import PageLoader from "../../../shared/PageLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Link, usePath, useHistory, navigate } from "raviger";
import Table, {
  CellTmpl,
  DEFAULT_PAYLOAD,
  TitleTmpl,
  reactFormatter,
} from "../../../common/Table";
import {
  STATUS_MAPPING,
  TOOLTIP_DELAY,
} from "../../../common/Constants";
import {
  useGetAllVendorAudits,
} from "../../../../backend/query";
import Icon from "../../../common/Icon";
import {
  downloadFileContent,
  preventDefault
} from "../../../../utils/common";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import TableActions, { ActionButton } from "../../../common/TableActions";
import AdminVendorLocations from "../../Masters/Companies/AdminVendorLocation";
import VendorAuditBulkDownloadModal from "./VendorAuditBulkDownloadModal";
import { getAuditReportFileName, useAuditReportForVendor } from "../../../../backend/exports";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import { AUDIT_STATUS_COLOR_CODES, AUDIT_STATUS_LABELS, AUDIT_STATUS as VENDOR_AUDIT_STATUS } from "../../../../utils/auditStateMachine";

const SortFields: any = {
  "company.name": "company_name",
  "associate_company.name": "associate_company_name",
  "location.name": "location_name",
};

function VendorAuditList() {
  const { state }: any = useHistory();
  const path: any = usePath();
  const [fromDashboard] = useState(path.includes("/dashboard/activities"));
  const [data, setData] = useState<any>();
  const [params, setParams] = useState<any>();
  const [locationFilters, setLocationFilter] = useState<any>();
  const lfRef: any = useRef();
  lfRef.current = locationFilters;
  const [payload, setPayload] = useState<any>();
  const payloadRef = useRef();
  payloadRef.current = payload;
  const [showLoader, setShowLoader] = useState(false);
  const [previousFilters, setPreviousFilters] = useState<any>(state || {});
  const previousFiltersRef = useRef();
  previousFiltersRef.current = previousFilters;
  const [enableBulkDownloadModal, setEnableBulkDownloadModal] = useState<boolean>(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const { activities, total, isFetching, refetch } = useGetAllVendorAudits(
    payload,
    Boolean(hasFilters(null, "company_id_eq"))
  );
  const { auditReportVendor, exporting } = useAuditReportForVendor(
      (response: any) => {
        const filename = selectedReport && selectedReport.name ? selectedReport.name.charAt(0).toUpperCase() + selectedReport.name.slice(1) : 'audit_report';
        if (response) {
          downloadFileContent({
            name: filename,
            type: response.headers["content-type"],
            content: response.data,
          });
        }
      },
      (error: any) => {
        console.log(error)
        toast.error(ERROR_MESSAGES.DEFAULT);
      }
    );

  const buttons: ActionButton[] = [
    {
      label: "Bulk Report",
      name: "bulkReport",
      privilege: USER_PRIVILEGES.REVIEWER_VENDOR_ACTIVITIES_DOWNLOAD_BULK_REPORT,
      action: () => {
        openBulkDownloadModal();
      },
    },
    {
      label: "Bulk Report",
      name: "bulkReport",
      privilege: USER_PRIVILEGES.SUBMITTER_VENDOR_ACTIVITIES_DOWNLOAD_BULK_REPORT,
      action: () => {
        openBulkDownloadModal();
      },
    }
  ];

  function onLocationChange(event: any, filter: any = {}) {
    // setData([]);
    setLocationFilter(event);
    if(Object.keys(filter).length > 0){
      const _filter = {
        ...previousFilters,
        ...filter
      }
      setPreviousFilters(_filter);
    }
  }

  async function downloadReport(audit: any) {
    if (audit && audit.id) {
      setShowLoader(true);
      try {
        let reportData: any;
        let auditData: any;
        if (audit && (audit.preliminary_report || audit.final_report)) {
          auditData = audit;
        } else {
          const response = await api.get(`/api/v1/vendor_location_audits/${audit.id}`, {
            include: ['preliminary_report', 'final_report'],
            includes: ['preliminary_report', 'final_report']
          });
          auditData = response.data;
        }
        if([VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_FINALIZED, VENDOR_AUDIT_STATUS.FINAL_AUDIT_PENDING, VENDOR_AUDIT_STATUS.FINAL_AUDIT_SUBMITTED, VENDOR_AUDIT_STATUS.FINAL_AUDIT_EDITING].includes(audit.status)){
          reportData = auditData.preliminary_report;
        } else if([VENDOR_AUDIT_STATUS.FINAL_AUDIT_PUBLISHED, VENDOR_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED].includes(audit.status)){
          reportData = auditData.final_report;
        }
        setSelectedReport(reportData);

        if (!reportData) {
          toast.warn(`No report available`);
          setShowLoader(false);
          return;
        }

        const { s3_region, s3_key, s3_bucket } = reportData || {};
        if (s3_region && s3_key && s3_bucket) {
          auditReportVendor({
            s3_region,
            s3_key,
            s3_bucket
          });
        } else {
          toast.warn(`No report available`);
        }
      } catch (error) {
        console.error('Error fetching audit:', error);
      }
      setShowLoader(false);
    }
  }

  function MonthTmpl({ cell }: any) {
    const row = cell.getData();
    return (
      <div className="d-flex align-items-center position-relative h-100">
        {row.month} ({row.year})
      </div>
    );
  }

  function ActionColumnElements({ cell }: any) {
    const row = cell.getData();
    return (
      <div className="d-flex flex-row align-items-center position-relative h-100">
        <Icon
          className="ms-1"
          type="button"
          name="download"
          text="Download"
          data={row}
          action={downloadReport}
        />
      </div>
    );
  }

  const columns = [
    {
      title: "Month & Year",
      field: "month",
      headerSort: false,
      width: 140,
      formatter: reactFormatter(<MonthTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      headerClick: (e: any, col: any) => {
        preventDefault(e);
      },
    },
    {
      title: "Company",
      field: "company.name",
      headerSort: false,
      widthGrow: 2,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Associate Company",
      field: "associate_company.name",
      headerSort: false,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      widthGrow: 1,
    },
    {
      title: "Location Name",
      field: "location.name",
      headerSort: false,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      widthGrow: 1,
    },
    {
      title: "Vendor Category",
      field: "vendor_category.name",
      headerSort: false,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      widthGrow: 1,
    },
    {
      title: "Vendor",
      field: "vendor_registration.vendor_name",
      headerSort: false,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      widthGrow: 1,
    },
    {
      title: "Status",
      field: "status",
      headerSort: false,
      formatter: reactFormatter(<AuditStatusTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      widthGrow: 1
    },
    {
      title: "Actions",
      hozAlign: "center",
      width: 100,
      headerSort: false,
      formatter: reactFormatter(<ActionColumnElements />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
  ];

  function AuditStatusTmpl({ cell }: any) {
    const status: any = cell.getValue();
    return (
      <div className="d-flex align-items-center position-relative h-100">
        {status && (
          <OverlayTrigger overlay={<Tooltip>{AUDIT_STATUS_LABELS[status as keyof typeof AUDIT_STATUS_LABELS] || status}</Tooltip>} rootClose={true}
              placement="bottom" delay={{ show: TOOLTIP_DELAY } as any}>
              <div className={`ellipse two-lines ${AUDIT_STATUS_COLOR_CODES[status as keyof typeof AUDIT_STATUS_COLOR_CODES] || 'text-dark-gray'}`}>{AUDIT_STATUS_LABELS[status as keyof typeof AUDIT_STATUS_LABELS] || status}</div>
          </OverlayTrigger>
        )}
      </div>
    );
  }

  const [tableConfig] = useState({
    paginationMode: "remote",
    ajaxRequestFunc,
    columns,
    rowHeight: 48,
    selectable: false ,
    paginate: true,
    initialSort: ["month desc"],
    rowFormatter,
  });

  function rowFormatter(row: any) {
    const data = row.getData();
    const element = row.getElement();
    if (data.published) {
      // element.classList.add("activity-published");
    }
  }

  function formatApiResponse(params: any, list: any, totalRecords: number) {
    const { page, per } = params || {};
    const tdata = {
      data: list,
      total: totalRecords,
      last_page: Math.ceil(totalRecords / (per || 1)) || 1,
      page: page || 1,
    };
    return tdata;
  }

  function hasFilters(ref: any, field = "company_id_eq") {
    const _filters: any =
      (ref
        ? ref.current
        : ({ ...(payloadRef.current || {}) } as any)["filters"]) || {};
    return _filters[field];
  }

  function ajaxRequestFunc(url: any, config: any, params: any) {
    const { field, dir } = (params.sort || [])[0] || {};
    const _params = {
      per: params.size,
      page: params.page,
      include: ['company', 'associate_company', 'location', 'vendor_category', 'vendor_registration', 'preliminary_report', 'final_report'],
      includes: ['company', 'associate_company', 'location', 'vendor_category', 'vendor_registration', 'preliminary_report', 'final_report']
    };
    setParams(_params);
    setPayload({
      ...DEFAULT_PAYLOAD,
      ..._params,
      filters: {
        ...(lfRef.current || {}),
        status_not_eq: VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_CREATED
      },
    });
    return Promise.resolve(formatApiResponse(params, activities, total));
  }

  function handlePageNav(_pagination: any) {
    const _params = { ...params, ..._pagination };
    setParams({ ..._params });
    setPayload({ ...(payload as any), ..._params });
  }

  useEffect(() => {
    if (locationFilters) {
      setPayload({
        ...DEFAULT_PAYLOAD,
        ...params,
        filters: {
          ...locationFilters,
          status_not_eq: VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_CREATED
        },
      });
    }
  }, [locationFilters]);

  useEffect(() => {
    if (!isFetching && payload) {
      setData(formatApiResponse(params, activities, total));
    }
  }, [isFetching]);

  function rowClick(e:any, selected: any) {
    const audit = selected.getData() || {};
    if (audit.id) {
      navigate(`/vendor-audits/${audit.id}`, {
        state: {
          ...(previousFiltersRef.current || {})
        }
      });
    }
  }

  function openBulkDownloadModal() {
    setEnableBulkDownloadModal(true)
  }

  return (
    <>
      <div className="d-flex flex-column">
        <div className="d-flex  p-2 align-items-center pageHeading shadow">
          <h4 className="mb-0">Vendor Audits</h4>
          <div className="d-flex align-items-end h-100">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0 d-flex justify-content-end">
                <li className="breadcrumb-item">Home</li>
                {fromDashboard && (
                  <li className="breadcrumb-item fw-bold">
                    <Link href="/dashboard">Dashboard</Link>
                  </li>
                )}
                <li className="breadcrumb-item  fw-bold active">
                  Audit Compliances
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <form className="p-0 mx-3 my-2">
          <div className="card shadow border-0 p-2 mt-2 mb-3">
            <div className="d-flex justify-content-between">
              <div className="d-flex flex-row align-items-center filters">
                <AdminVendorLocations onChange={onLocationChange} previousFilters={previousFilters} />
              </div>
              <TableActions buttons={buttons} />
            </div>
          </div>
        </form>
        <div className="position-relative clickable">
          <Table
            data={data}
            options={tableConfig}
            onPageNav={handlePageNav}
            rowClick={rowClick}
          />
        </div>
      </div>
      {enableBulkDownloadModal && (
        <VendorAuditBulkDownloadModal
        onSubmit={() => setEnableBulkDownloadModal(false)}
        onCancel={() => { setEnableBulkDownloadModal(false) }}
        previousFilters={previousFilters} />
      )}
      {(showLoader || isFetching) && <PageLoader />}
    </>
  );
}

export default VendorAuditList;
